import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CartSidebar from '@/components/feature/CartSidebar';

const TEAM = [
  {
    name: 'Derek Swanson',
    role: 'Founder & CEO',
    bio: 'Former wilderness guide with 15 years in the Sonoran Desert. Derek founded SWIFTMARKET after struggling to find reliable gear for desert-to-alpine expeditions.',
    avatar: 'https://public.readdy.ai/ai/img_res/6e7373937c6acdcf0a021b085523af91.jpg',
  },
  {
    name: 'Maya Chen',
    role: 'Head of Product',
    bio: 'Ultramarathon runner and gear obsessive. Maya tests every product in our catalog personally — from the Sonoran Desert to the Colorado Rockies.',
    avatar: 'https://readdy.ai/api/search-image?query=female%20outdoor%20athlete%20portrait%20smiling%20with%20mountain%20trail%20background%2C%20professional%20headshot%20natural%20light%2C%20athletic%20confident%20look%2C%20early%2030s&width=300&height=300&seq=5002&orientation=squarish',
  },
  {
    name: 'Rafe Torres',
    role: 'Lead Gear Technician',
    bio: 'Certified mountain guide and materials engineer. Rafe oversees quality control and ensures every product meets our extreme-condition standards.',
    avatar: 'https://readdy.ai/api/search-image?query=male%20outdoor%20gear%20technician%20portrait%20with%20climbing%20equipment%20background%2C%20professional%20headshot%20natural%20light%2C%20rugged%20confident%20look%2C%20late%2030s&width=300&height=300&seq=5003&orientation=squarish',
  },
  {
    name: 'Priya Nair',
    role: 'Customer Experience',
    bio: 'Avid backpacker and Leave No Trace advocate. Priya leads our support team and makes sure every adventurer gets the right gear for their mission.',
    avatar: 'https://readdy.ai/api/search-image?query=female%20customer%20service%20professional%20portrait%20smiling%20warmly%20with%20outdoor%20nature%20background%2C%20professional%20headshot%20natural%20light%2C%20friendly%20approachable%20look%2C%20early%2030s&width=300&height=300&seq=5004&orientation=squarish',
  },
];

const VALUES = [
  {
    icon: 'ri-shield-check-line',
    title: 'Uncompromising Quality',
    desc: 'Every product in our catalog is field-tested in real conditions — desert heat, alpine cold, and everything in between. If it doesn\'t perform, it doesn\'t make the cut.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Sustainability First',
    desc: 'We partner with brands committed to responsible manufacturing. From recycled materials to carbon-offset shipping, we\'re working to protect the wild places we love.',
  },
  {
    icon: 'ri-group-line',
    title: 'Community Driven',
    desc: 'Our reviews, guides, and gear picks come from real adventurers. We\'re a community of explorers helping each other get out there safely and confidently.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Expert Support',
    desc: 'Our team are active outdoor enthusiasts, not just sales reps. Call, email, or chat — we\'ll help you find exactly the right gear for your next adventure.',
  },
];

const MILESTONES = [
  { year: '2018', event: 'SWIFTMARKET LLC founded in Phoenix, AZ by Derek Swanson' },
  { year: '2019', event: 'Launched online store with 200+ curated outdoor products' },
  { year: '2020', event: 'Reached 10,000 customers across the Southwest' },
  { year: '2021', event: 'Expanded catalog to 1,000+ products, added gear rental program' },
  { year: '2023', event: 'Opened Phoenix flagship store at 2825 W Berridge Ln' },
  { year: '2024', event: 'Surpassed 48,000 happy adventurers served nationwide' },
  { year: '2026', event: 'Launching exclusive SWIFTMARKET branded gear line' },
];

export default function AboutPage() {
  return (
    <div className="font-inter min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dramatic%20wide%20angle%20aerial%20view%20of%20rugged%20Arizona%20desert%20canyon%20landscape%20at%20golden%20hour%20with%20warm%20orange%20and%20red%20rock%20formations%20stretching%20to%20distant%20mountain%20horizon%2C%20cinematic%20landscape%20photography%20no%20people%2C%20vast%20wilderness&width=1920&height=700&seq=6001&orientation=landscape"
            alt="Arizona wilderness"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/50 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-6 md:px-12 pb-16 md:pb-24">
          <span className="inline-block bg-[#C4FF61] text-[#1A1A1A] font-outfit font-semibold text-xs px-4 py-1.5 rounded-full uppercase tracking-widest mb-5">
            Our Story
          </span>
          <h1 className="font-outfit font-black text-white text-4xl md:text-6xl lg:text-7xl leading-none mb-4">
            BORN IN THE<br /><span className="text-[#C4FF61]">DESERT.</span>
          </h1>
          <p className="text-white/70 font-inter text-base md:text-lg max-w-xl leading-relaxed">
            From Phoenix trails to alpine summits — SWIFTMARKET was built by adventurers, for adventurers.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="px-6 md:px-12 py-20 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-outfit font-black text-[#1A1A1A] text-2xl md:text-4xl leading-snug">
            &ldquo;We believe the right gear doesn&apos;t just make adventures safer — it makes them <span className="text-[#C4FF61] bg-[#1A1A1A] px-2 rounded">possible.</span>&rdquo;
          </p>
          <p className="font-inter text-gray-500 text-base mt-6 leading-relaxed max-w-2xl mx-auto">
            SWIFTMARKET LLC was founded in 2018 with a simple mission: give every outdoor enthusiast access to premium, field-tested gear at honest prices. No fluff, no gimmicks — just gear that works when it matters most.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#1A1A1A] py-14 px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {[
            { value: '2,400+', label: 'Products Curated' },
            { value: '48,000+', label: 'Happy Adventurers' },
            { value: '4.9★', label: 'Average Rating' },
            { value: '8 Years', label: 'In Business' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-outfit font-black text-[#C4FF61] text-3xl md:text-4xl">{s.value}</p>
              <p className="font-inter text-white/40 text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Our Story */}
      <section className="px-6 md:px-12 py-20 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto">
          <div>
            <span className="inline-block bg-[#C4FF61]/20 text-[#5a7a00] font-outfit font-semibold text-xs px-3 py-1.5 rounded-full uppercase tracking-widest mb-5">
              How It Started
            </span>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl leading-tight mb-6">
              A Wilderness Guide&apos;s Frustration Became a Business
            </h2>
            <div className="flex flex-col gap-4 font-inter text-gray-600 text-sm leading-relaxed">
              <p>
                In 2017, Derek Swanson was leading a 10-day expedition through the Superstition Wilderness when his jacket failed on day three. Soaked and cold at 7,000 feet, he realized the outdoor gear market was full of products that looked great in catalogs but fell apart in the field.
              </p>
              <p>
                Back in Phoenix, Derek spent a year testing hundreds of products across the Sonoran Desert, the Colorado Plateau, and the Arizona Highlands. He built relationships with manufacturers who shared his obsession with real-world performance.
              </p>
              <p>
                In 2018, SWIFTMARKET LLC opened its doors at 2825 W Berridge Ln, Phoenix — a small warehouse with a big mission. Today, we serve adventurers across all 50 states with the same uncompromising standard: if it doesn&apos;t perform in the field, it doesn&apos;t belong in our store.
              </p>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold px-7 py-3.5 rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer whitespace-nowrap text-sm mt-8"
            >
              Shop Our Gear
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=outdoor%20gear%20store%20interior%20with%20organized%20shelves%20of%20hiking%20equipment%20backpacks%20jackets%20and%20boots%2C%20warm%20industrial%20lighting%2C%20clean%20modern%20retail%20space%20with%20exposed%20brick%20and%20wood%20accents%2C%20inviting%20atmosphere&width=700&height=600&seq=6002&orientation=landscape"
              alt="SWIFTMARKET store interior"
              className="w-full h-[420px] object-cover object-top rounded-2xl"
            />
            <div className="absolute -bottom-5 -left-5 bg-[#C4FF61] rounded-2xl px-5 py-4 hidden md:block">
              <p className="font-outfit font-black text-[#1A1A1A] text-2xl">2018</p>
              <p className="font-inter text-[#1A1A1A]/70 text-xs">Founded in Phoenix, AZ</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F7F8F9] px-6 md:px-12 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#C4FF61]/20 text-[#5a7a00] font-outfit font-semibold text-xs px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
              What We Stand For
            </span>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 flex flex-col gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A1A1A]">
                  <i className={`${v.icon} text-[#C4FF61] text-xl`}></i>
                </div>
                <h3 className="font-outfit font-bold text-[#1A1A1A] text-base">{v.title}</h3>
                <p className="font-inter text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 md:px-12 py-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#C4FF61]/20 text-[#5a7a00] font-outfit font-semibold text-xs px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
              Our Journey
            </span>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl">Milestones</h2>
          </div>
          <div className="relative">
            <div className="absolute left-[72px] top-0 bottom-0 w-px bg-gray-100 hidden sm:block"></div>
            <div className="flex flex-col gap-6">
              {MILESTONES.map((m, i) => (
                <div key={i} className="flex items-start gap-5 sm:gap-8">
                  <div className="shrink-0 w-14 sm:w-16 text-right">
                    <span className="font-outfit font-black text-[#C4FF61] text-sm">{m.year}</span>
                  </div>
                  <div className="relative shrink-0 hidden sm:flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-[#C4FF61] border-2 border-white ring-2 ring-[#C4FF61]/30 mt-0.5"></div>
                  </div>
                  <div className="flex-1 pb-2">
                    <p className="font-inter text-gray-600 text-sm leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-[#F7F8F9] px-6 md:px-12 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#C4FF61]/20 text-[#5a7a00] font-outfit font-semibold text-xs px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
              The People Behind the Gear
            </span>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl">Meet the Team</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((member) => (
              <div key={member.name} className="bg-white rounded-2xl overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-56 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-5">
                  <h3 className="font-outfit font-bold text-[#1A1A1A] text-base">{member.name}</h3>
                  <p className="font-outfit text-[#C4FF61] text-xs font-semibold mb-2">{member.role}</p>
                  <p className="font-inter text-gray-500 text-xs leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="px-6 md:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block bg-[#C4FF61]/20 text-[#5a7a00] font-outfit font-semibold text-xs px-3 py-1.5 rounded-full uppercase tracking-widest mb-5">
              Visit Us
            </span>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl leading-tight mb-6">
              Come See Us in Phoenix
            </h2>
            <div className="flex flex-col gap-4 mb-8">
              {[
                { icon: 'ri-map-pin-line', label: 'Address', value: '2825 W Berridge Ln, Phoenix, AZ 85017' },
                { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Sat: 9am–7pm · Sun: 10am–5pm' },
                { icon: 'ri-phone-line', label: 'Phone', value: '(602) 555-0184' },
                { icon: 'ri-mail-line', label: 'Email', value: 'hello@swiftmarket.com' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1A1A1A] shrink-0">
                    <i className={`${item.icon} text-[#C4FF61] text-base`}></i>
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-[#1A1A1A] text-sm">{item.label}</p>
                    <p className="font-inter text-gray-500 text-sm">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#C4FF61] font-outfit font-bold px-7 py-3.5 rounded-full hover:bg-black transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-store-line"></i>
              Shop Online
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden h-[380px]">
            <iframe
              title="SWIFTMARKET LLC Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3329.5!2d-112.1!3d33.49!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b0e0000000001%3A0x1!2s2825+W+Berridge+Ln%2C+Phoenix%2C+AZ+85017!5e0!3m2!1sen!2sus!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] px-6 md:px-12 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-outfit font-black text-white text-3xl md:text-4xl mb-4">
            Ready to Gear Up?
          </h2>
          <p className="font-inter text-white/50 text-base mb-8 leading-relaxed">
            Browse our full catalog of field-tested outdoor gear. Free shipping on orders over $75.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold px-10 py-4 rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            Shop All Gear
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
      <CartSidebar />
    </div>
  );
}
